/**
 * Work-week day ordering for staff augmentation timesheets.
 *
 * Daily hours (standardHoursDetails, nonStandardHoursDetails, doubleTimeHoursDetails and
 * their per-allocation equivalents) are ALWAYS persisted Monday-first — index 0 is Monday
 * through index 6 for Sunday — no matter which day an organization's work week starts on.
 *
 * An organization's contract work periods carry `initialWeekday` (Ruby wday: 0 = Sunday,
 * 1 = Monday), surfaced on a pay period as `workPeriodInitialWeekday`. That only affects
 * PRESENTATION: the first column of every daily breakdown should be the first day of the
 * cycle period. Nothing here reorders what gets stored.
 */

// Canonical storage order. Index into a *HoursDetails array.
export const WEEK_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const WEEKEND_DAYS = ["saturday", "sunday"];

export const MONDAY = 1;

// Column header text, keyed by day.
export const DAY_HEADERS = {
  monday: "Mon.",
  tuesday: "Tue.",
  wednesday: "Wed.",
  thursday: "Thu.",
  friday: "Fri.",
  saturday: "Sat.",
  sunday: "Sun.",
};

// Compact header text used in the hours popovers.
export const SHORT_DAY_HEADERS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

// Abbreviations used in CSV exports.
export const CSV_DAY_HEADERS = {
  monday: "mon",
  tuesday: "tues",
  wednesday: "wed",
  thursday: "thurs",
  friday: "fri",
  saturday: "sat",
  sunday: "sun",
};

export function normalizeInitialWeekday(initialWeekday) {
  // Number(null) and Number("") are both 0 -- an absent value must fall back to Monday,
  // not silently rotate the whole grid to a Sunday start.
  if (initialWeekday == null || initialWeekday === "") return MONDAY;

  const wday = Number(initialWeekday);
  return Number.isInteger(wday) && wday >= 0 && wday <= 6 ? wday : MONDAY;
}

// Ruby wday (0 = Sunday) -> index into WEEK_DAYS (0 = Monday).
const storageIndexForWday = (wday) => (wday + 6) % 7;

/**
 * Storage indices in the order they should be displayed.
 * Monday start -> [0,1,2,3,4,5,6]; Sunday start -> [6,0,1,2,3,4,5].
 */
export function dayOrder(initialWeekday) {
  const start = normalizeInitialWeekday(initialWeekday);
  return WEEK_DAYS.map((_, i) => storageIndexForWday((start + i) % 7));
}

/** Day keys in the order they should be displayed. */
export function orderedDays(initialWeekday) {
  return dayOrder(initialWeekday).map((index) => WEEK_DAYS[index]);
}

/**
 * Reorder a Monday-first array of 7 daily values into display order.
 * Returns an empty array for an absent/empty source so callers can keep
 * treating "no details" as "render no cells".
 */
export function toDisplayOrder(details, initialWeekday) {
  if (!details || details.length === 0) return [];
  return dayOrder(initialWeekday).map((index) => details[index]);
}

/** DataGrid column definitions for the timesheet hours grids. */
export function buildTimesheetColumns(initialWeekday) {
  return [
    {
      field: "id", headerName: "Hours", width: 120, sortable: false
    },
    ...orderedDays(initialWeekday).map((day) => ({
      field: day,
      headerName: DAY_HEADERS[day],
      width: 73,
      editable: true,
      sortable: false,
      align: "center",
      headerAlign: "center",
    })),
    {
      field: "total", headerName: "Total", type: "number", width: 84, sortable: false, align: "center", headerAlign: 'center',
    },
  ];
}
